import { Emitter } from '@socket.io/component-emitter';
import { bytesOf, decodeRoad } from '../domain/roadProtocol.js';

export const WIRE_CODEC = 'binary-zstd-v1';
export const MAX_PAYLOAD = 1000000;
export const WIRE_HEADER_SIZE = 9;
export const COMPRESSION_THRESHOLD = 512;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder('utf-8', { fatal: true });
const sensitiveKey = /"[^"\\]*(?:password|token|ticket|nonce|signature|secret|cookie|authorization)[^"\\]*"\s*:/i;
const reservedEvents = new Set(['connect', 'connect_error', 'disconnect', 'disconnecting', 'newListener', 'removeListener']);

function validate(packet) {
  if (!packet || packet.nsp !== '/' || packet.attachments !== undefined || (packet.id !== undefined && (!Number.isSafeInteger(packet.id) || packet.id < 0))) throw new Error('Invalid socket packet metadata');
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  switch (packet.type) {
    case 0: if (packet.data !== undefined && !object(packet.data)) throw new Error('Invalid CONNECT'); break;
    case 1: if (packet.data !== undefined) throw new Error('Invalid DISCONNECT'); break;
    case 2: if (!Array.isArray(packet.data) || typeof packet.data[0] !== 'string' || !packet.data[0] || reservedEvents.has(packet.data[0]) || packet.data[0] === 'Road') throw new Error('Invalid EVENT'); break;
    case 3: if (!Array.isArray(packet.data)) throw new Error('Invalid ACK'); break;
    case 4: if (!object(packet.data)) throw new Error('Invalid CONNECT_ERROR'); break;
    default: throw new Error('Invalid socket packet type');
  }
}

// Validate a single bounded frame BEFORE the WASM library allocates its output.
// Reject unknown sizes, dictionaries, skippable/concatenated frames and large
// windows; the declared WLZ1 size alone must not be trusted.
export function validateZstdFrame(bytes, expectedSize) {
  if (bytes.length < 6 || bytes[0] !== 0x28 || bytes[1] !== 0xb5 || bytes[2] !== 0x2f || bytes[3] !== 0xfd) throw new Error('Invalid zstd frame magic');
  const descriptor = bytes[4];
  if (descriptor & 0x18) throw new Error('Invalid zstd descriptor');
  const single = Boolean(descriptor & 0x20);
  let offset = 5;
  if (!single) {
    const window = bytes[offset++];
    const base = 2 ** (10 + (window >>> 3));
    if (base + base / 8 * (window & 7) > 2 ** 20) throw new Error('Zstd window exceeds limit');
  }
  const dictionarySize = [0, 1, 2, 4][descriptor & 3];
  for (let i = 0; i < dictionarySize; i++) if (bytes[offset++] !== 0) throw new Error('Zstd dictionaries are not supported');
  const flag = descriptor >>> 6;
  const contentSizeBytes = [single ? 1 : 0, 2, 4, 8][flag];
  if (!contentSizeBytes || offset + contentSizeBytes > bytes.length) throw new Error('Missing zstd content size');
  let size = 0n;
  for (let i = 0; i < contentSizeBytes; i++) size |= BigInt(bytes[offset++]) << BigInt(i * 8);
  if (flag === 1) size += 256n;
  if (size !== BigInt(expectedSize) || size < 1n || size > BigInt(MAX_PAYLOAD)) throw new Error('Zstd content size mismatch');
  let last = false;
  while (!last) {
    if (offset + 3 > bytes.length) throw new Error('Truncated zstd block');
    const header = bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16;
    offset += 3;
    last = Boolean(header & 1);
    const type = (header >>> 1) & 3;
    if (type === 3) throw new Error('Invalid zstd block type');
    offset += type === 1 ? 1 : header >>> 3;
    if (offset > bytes.length) throw new Error('Truncated zstd block body');
  }
  if (descriptor & 4) offset += 4;
  if (offset !== bytes.length) throw new Error('Trailing/truncated zstd frame');
}

export function createSocketWireParser({ compress, decompress, onMetric = () => {} }) {
  class Encoder {
    encode(packet) {
      let kind = 0;
      let plain;
      if (packet.type === 2 && packet.data?.[0] === 'Road') {
        if (packet.nsp !== '/' || packet.id !== undefined || packet.data.length !== 2) throw new Error('Invalid Road envelope');
        plain = bytesOf(packet.data[1]);
        decodeRoad(plain);
        kind = 2;
      } else {
        validate(packet);
        plain = textEncoder.encode(JSON.stringify(packet));
      }
      if (!plain.length || plain.length > MAX_PAYLOAD) throw new Error('Socket payload exceeds limit');
      let body = plain;
      if (plain.length >= COMPRESSION_THRESHOLD && (kind === 2 || !sensitiveKey.test(textDecoder.decode(plain)))) {
        const compressed = compress(plain, 1);
        if (compressed.length < plain.length) { body = compressed; kind++; }
      }
      const frame = new Uint8Array(WIRE_HEADER_SIZE + body.length);
      frame.set([87, 76, 90, 49, kind]);
      new DataView(frame.buffer).setUint32(5, plain.length);
      frame.set(body, 9);
      onMetric({ direction: 'send', kind, plainBytes: plain.length, wireBytes: frame.length });
      return [frame];
    }
  }

  class Decoder extends Emitter {
    add(value) {
      const frame = bytesOf(value);
      if (frame.length < WIRE_HEADER_SIZE || frame.length > MAX_PAYLOAD + WIRE_HEADER_SIZE || frame[0] !== 87 || frame[1] !== 76 || frame[2] !== 90 || frame[3] !== 49 || frame[4] > 3) throw new Error('Invalid binary-zstd-v1 frame');
      const kind = frame[4];
      const size = new DataView(frame.buffer, frame.byteOffset, frame.byteLength).getUint32(5);
      if (!size || size > MAX_PAYLOAD) throw new Error('Socket decoded size exceeds limit');
      let plain = frame.subarray(9);
      if (kind & 1) {
        validateZstdFrame(plain, size);
        plain = decompress(plain, { defaultHeapSize: size });
      }
      if (plain.length !== size) throw new Error('Socket decoded length mismatch');
      let packet;
      if (kind & 2) {
        decodeRoad(plain);
        packet = { type: 2, nsp: '/', data: ['Road', plain] };
      } else {
        packet = JSON.parse(textDecoder.decode(plain));
        validate(packet);
      }
      onMetric({ direction: 'receive', kind, plainBytes: plain.length, wireBytes: frame.length });
      this.emit('decoded', packet);
    }
    destroy() { this.off(); }
  }
  return { protocol: 5, Encoder, Decoder };
}
